import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatCalendarCellClassFunction } from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Activity, Appointment, PersonAppointmentRoutineTask } from "src/app/models/appointment";
import { BehaviorSubject, Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import jsPDF from "jspdf";
import { Modal } from "bootstrap";
import { Person, ContractType } from "src/app/models/person";
import { monthlyTag, Tag } from "src/app/models/tag";
import { PersonService } from "src/app/services/person.service";
import { TagService } from "src/app/services/tag.service";
import { Urls } from "@config/urls.config";
import { AppointmentService } from "@services/appointment.service";
import { PersonAppointmentDialogComponent } from "@pages/person/person-appointment/person-appointment-dialog/person-appointment-dialog.component";
import { LoggerService } from "@shared/logger.service";
import { DatePipe } from "@angular/common";
// ErrorHandlerService importado abaixo
import { AuthService } from "src/app/services/auth.service";
import { ChatService, Message } from "../../services/chat.service";
import { PersonAppointmentConfirmComponent } from "src/app/pages/person/person-appointment/person-appointment-confirm/person-appointment-confirm.component";
import { TasksService } from "src/app/features/tasks/services/tasks.service";
// Novos services
import { CalendarService } from "src/app/services/calendar.service";
import { ModalService } from "src/app/services/modal.service";
import { AppointmentStateService } from "src/app/services/appointment-state.service";
import { TagManagementService } from "src/app/services/tag-management.service";
import { ValidationService } from "src/app/services/validation.service";
import { ErrorHandlerService } from "src/app/services/error-handler.service";
import { CacheService } from "src/app/services/cache.service";
import { GoalsService } from "src/app/features/goals/services/goals.service";

// Interface para o modal de atestados
import { AbstinenceData } from "../../../../shared/components/abstinence-modal/abstinence-modal.interface";
// Interface para o modal de férias
import { VacationData } from "../../../../shared/components/vacation-modal/vacation-modal.interface";
// Interfaces e constantes
import {
  AppointmentComponentState,
  AppointmentFormData,
  AbstinenceFormData,
  VacationFormData,
  MonthlyCalendarData,
  MonthlyTagData,
  FilterData,
} from "src/app/interfaces/appointment.interface";
import {
  APPOINTMENT_TYPES,
  APPOINTMENT_TYPE_LABELS,
  TAG_CONFIG,
  FORM_CONFIG,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  CALENDAR_CONFIG,
} from "src/app/constants/appointment.constants";
import { AbstinenceRequest, AbstinenceService } from "@app/services/abstinence.service";

export interface Goal {
  id?: string;
  personId?: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
}

@Component({
  selector: "app-employee-appointment",
  templateUrl: "./employee-appointment.component.html",
  styleUrls: ["./employee-appointment.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmployeeAppointmentComponent implements OnInit, AfterViewInit, OnDestroy {
  private pendingInitRequests = 0;
  isInitialDataReady = false;
  readonly initialLoad$ = new BehaviorSubject<boolean>(false);

  displayedColumns = ["name", "radio"];
  dataSource: Activity[];
  currentMonthTags: monthlyTag[] = [];
  lastMonthTags: monthlyTag[] = [];
  lastTwoMonthTags: monthlyTag[] = [];
  lastThreeMonthTags: monthlyTag[] = [];
  lastFourMonthTags: monthlyTag[] = [];
  lastFiveMonthTags: monthlyTag[] = [];
  daysOfMonth: number[] = [];
  daysOfLastMonth: number[] = [];
  daysOfLastTwoMonth: number[] = [];
  daysOfLastThreeMonth: number[] = [];
  daysOfLastFourMonth: number[] = [];
  daysOfLastFiveMonth: number[] = [];
  s3DefaultImage = Urls.getDefaultPictureS3();

  /**
   * Retorna a foto do funcionário
   */
  getEmployeePhoto(): string {
    const photo = this.person?.picture || "";

    // Se não há foto, retorna string vazia para mostrar o ícone padrão
    if (!photo || photo.trim() === "" || photo === "null" || photo === "undefined") {
      return "";
    }

    // Se a foto já é uma URL completa, retorna como está
    if (photo.startsWith("http://") || photo.startsWith("https://")) {
      return photo;
    }

    // Se a foto é apenas um nome de arquivo, constrói a URL completa
    return Urls.getS3() + photo;
  }

  // Constantes do componente
  readonly APPOINTMENT_TYPES = APPOINTMENT_TYPES;
  readonly APPOINTMENT_TYPE_LABELS = APPOINTMENT_TYPE_LABELS;
  readonly TAG_CONFIG = TAG_CONFIG;
  readonly FORM_CONFIG = FORM_CONFIG;
  readonly ERROR_MESSAGES = ERROR_MESSAGES;
  readonly SUCCESS_MESSAGES = SUCCESS_MESSAGES;

  // Estado do componente usando interface tipada
  private componentState: Partial<AppointmentComponentState> = {
    person: null,
    selected: null,
    selectedDateMonthly: null,
    selectedDateForModal: "",
    selectedActivityName: "",
    activities: [],
    appointments: [],
    tags: [],
    currentMonthTags: [],
    lastMonthTags: [],
    lastTwoMonthTags: [],
    lastThreeMonthTags: [],
    lastFourMonthTags: [],
    lastFiveMonthTags: [],
    daysOfMonth: [],
    daysOfLastMonth: [],
    daysOfLastTwoMonth: [],
    daysOfLastThreeMonth: [],
    daysOfLastFourMonth: [],
    daysOfLastFiveMonth: [],
    isLoading: false,
    isSavingAppointment: false,
    isSavingAbstinence: false,
    isSavingVacation: false,
  };

  // Dados convertidos para o modal de férias
  vacationsForModal: VacationData[] = [];

  /**
   * Converte VacationResponse[] para VacationData[]
   */
  // private convertVacationsToModalData(vacations: VacationResponse[]): VacationData[] {
  //   if (!vacations || vacations.length === 0) {
  //     return [];
  //   }

  //   return vacations.map((vacation) => ({
  //     id: vacation.id?.toString() || "",
  //     personId: this.personId || "",
  //     description: vacation.notes || "Sem descrição",
  //     startDate: vacation.startDate || "",
  //     endDate: vacation.endDate || "",
  //   }));
  // }

  // Dados de formulário tipados
  private appointmentFormData: AppointmentFormData = {
    id: "",
    personId: "",
    activityId: "",
    description: "",
    justification: "",
    createdAt: new Date(),
  };

  // Dados de calendário mensal tipados
  private monthlyCalendarData: MonthlyCalendarData[] = [];

  // Dados de filtros tipados
  private filterData: FilterData = {
    dateRange: {
      start: new Date(),
      end: new Date(),
    },
  };

  // Dados de tags mensais tipados
  private monthlyTagData: MonthlyTagData[] = [];
  personId: string;
  selected: Date | null;
  selectedDateMonthly: Date | null;
  activitiesResponse: Activity[] = [];
  activitiesDailyResponse: Activity[];
  // Coleções para seleção (Rotinas/Tarefas/Metas)
  tasksForList: Activity[] = [];
  goalsForList: Activity[] = [];

  // Controle do tipo de lista exibida
  selectedListType: "ROTINAS" | "TAREFAS" | "METAS" = "ROTINAS";

  // Controle do tipo de lista exibida no modal de calendário
  selectedModalListType: "ROTINAS" | "TAREFAS" | "METAS" = "ROTINAS";

  // Listas para o modal de calendário
  tasksForModal: Activity[] = [];
  goalsForModal: Activity[] = [];

  // Helper para mapear o tipo esperado pelo backend
  private mapSelectedListTypeToActivityType(listType?: "ROTINAS" | "TAREFAS" | "METAS"): "Routine" | "Task" | "GOAL" {
    const type = listType || this.selectedListType;
    switch (type) {
      case "TAREFAS":
        return "Task";
      case "METAS":
        return "GOAL";
      case "ROTINAS":
      default:
        return "Routine";
    }
  }

  // Getter para a lista a ser exibida no modal de calendário
  get displayedModalItems(): Activity[] {
    switch (this.selectedModalListType) {
      case "TAREFAS":
        return this.tasksForModal || [];
      case "METAS":
        return this.goalsForModal || [];
      default:
        return this.appointmentStateService.currentState.activities || [];
    }
  }

  // Referências do item/etiqueta selecionados para atualização pós-salva
  private lastSelectedActivityId?: string;
  private lastSelectedTag?: Tag;

  // Getter para a lista a ser exibida na aba Diária
  get displayedDailyItems(): Activity[] {
    switch (this.selectedListType) {
      case "TAREFAS":
        return this.tasksForList || [];
      case "METAS":
        return this.goalsForList || [];
      default:
        return this.activitiesDailyResponse || [];
    }
  }
  appointments: Appointment[] = [];
  tags: Tag[] = [];

  imageUrl: string | ArrayBuffer | null = null;
  firstDay: Date;
  lastDay: Date;
  firstDayLastMonth: Date;
  lastDayLastMonth: Date;
  firstDayLastTwoMonth: Date;
  lastDayLastTwoMonth: Date;
  firstDayLastThreeMonth: Date;
  lastDayLastThreeMonth: Date;
  firstDayLastFourMonth: Date;
  lastDayLastFourMonth: Date;
  firstDayLastFiveMonth: Date;
  lastDayLastFiveMonth: Date;
  person: Person;
  today: string = new Date().toLocaleDateString("pt-BR");
  startDate = new Date();
  endDate = new Date();
  selectedTab = new FormControl(0);
  maxLength = 255;

  abstinenceDocumentUrl: string | ArrayBuffer | null = null;
  vacationDocumentUrl: string | ArrayBuffer | null = null;
  selectedAbstinenceFile: File | null = null;
  selectedVacationFile: File | null = null;

  abstinences: AbstinenceFormData[] = [];
  vacations: VacationFormData[] = [];
  vacation: VacationFormData = {
    id: "",
    personId: "",
    description: "",
    startDate: new Date(),
    endDate: new Date(),
  };

  // vacationsV2: VacationResponse[];

  // Propriedades para estatísticas
  totalEvaluationsLast6Months: number = 0;
  evaluationsThisMonth: number = 0;

  // Configuração de opções usando constantes
  readonly options = [
    {
      index: APPOINTMENT_TYPES.TRAINING.toString(),
      color: TAG_CONFIG.COLORS.RED,
      description: TAG_CONFIG.DESCRIPTIONS.RED,
    },
    {
      index: APPOINTMENT_TYPES.PUNCTUALITY.toString(),
      color: TAG_CONFIG.COLORS.ORANGE,
      description: TAG_CONFIG.DESCRIPTIONS.ORANGE,
    },
    {
      index: APPOINTMENT_TYPES.ATTENDANCE.toString(),
      color: TAG_CONFIG.COLORS.YELLOW,
      description: TAG_CONFIG.DESCRIPTIONS.YELLOW,
    },
    { index: "4", color: TAG_CONFIG.COLORS.GREEN, description: TAG_CONFIG.DESCRIPTIONS.GREEN },
    { index: "5", color: TAG_CONFIG.COLORS.BLUE, description: TAG_CONFIG.DESCRIPTIONS.BLUE },
    { index: "6", color: TAG_CONFIG.COLORS.PURPLE, description: TAG_CONFIG.DESCRIPTIONS.PURPLE },
  ];

  formAppointment: FormGroup;
  formAbstinence: FormGroup;
  formVacation: FormGroup;
  formReport: FormGroup;

  chatForm!: FormGroup;

  messages: Message[] = [];
  page = 0;
  size = 20;
  loading = false;
  userA = "user123";
  userB = "user456";

  isSavingAppointment: boolean = false;
  isSavingAbstinence: boolean = false;
  isSavingVacation: boolean = false;
  isLoadingReport: boolean = false;
  reportPeriod: { startDate: string; endDate: string } = { startDate: "", endDate: "" };

  // Variáveis para exibir informações no modal
  // Propriedades para exibição nos modais - agora usando observables do AppointmentStateService
  get selectedDateForModal$() {
    return this.appointmentStateService.selectedDateForModal$;
  }
  get selectedActivityName$() {
    return this.appointmentStateService.selectedActivityName$;
  }

  /**
   * Retorna o label correto baseado no tipo de contrato da pessoa
   */
  getPersonTypeLabel(): string {
    if (!this.person?.contractType) {
      return "Funcionário";
    }

    const contractType = this.person.contractType.toLowerCase().trim();

    // Verifica se é CLT
    if (contractType === ContractType.CLT.toLowerCase() || contractType === "clt") {
      return "Funcionário";
    }

    // Verifica se é Autônomo
    if (
      contractType === ContractType.AUTONOMO.toLowerCase() ||
      contractType === "autônomo" ||
      contractType === "autonomo" ||
      contractType === "profissional"
    ) {
      return "Profissional Autônomo";
    }

    // Verifica se é Prestador de Serviço/Fornecedor
    if (
      contractType === ContractType.SUPPLIER.toLowerCase() ||
      contractType === "fornecedor" ||
      contractType === "prestador de serviço" ||
      contractType === "prestador" ||
      contractType === "supplier"
    ) {
      return "Prestador de Serviço";
    }

    // Default
    return "Funcionário";
  }
  get activities$() {
    return this.appointmentStateService.activities$;
  }

  // TrackBy functions para performance
  trackByActivityId = (index: number, activity: Activity): string => activity.id;
  trackByTagId = (index: number, tag: Tag): string => tag.id;
  trackByAppointmentId = (index: number, appointment: Appointment): string => appointment.id;
  trackByMessageId = (index: number, message: Message): string => message.id?.toString() || index.toString();
  // trackByVacationId = (index: number, vacation: VacationResponse): string =>
  //   vacation.id?.toString() || index.toString();

  // Métodos para gerenciar estado do componente usando interfaces
  private updateComponentState(updates: Partial<AppointmentComponentState>): void {
    this.componentState = { ...this.componentState, ...updates };
  }

  private getComponentState(): Partial<AppointmentComponentState> {
    return this.componentState;
  }

  // Métodos para gerenciar dados de formulário
  private updateAppointmentFormData(data: Partial<AppointmentFormData>): void {
    this.appointmentFormData = { ...this.appointmentFormData, ...data };
  }

  private getAppointmentFormData(): AppointmentFormData {
    return this.appointmentFormData;
  }

  // Métodos para gerenciar dados de calendário mensal
  private updateMonthlyCalendarData(data: MonthlyCalendarData[]): void {
    this.monthlyCalendarData = data;
  }

  private getMonthlyCalendarData(): MonthlyCalendarData[] {
    return this.monthlyCalendarData;
  }

  // Métodos para gerenciar dados de filtros
  private updateFilterData(data: Partial<FilterData>): void {
    this.filterData = { ...this.filterData, ...data };
  }

  private getFilterData(): FilterData {
    return this.filterData;
  }

  // Métodos para gerenciar dados de tags mensais
  private updateMonthlyTagData(data: MonthlyTagData[]): void {
    this.monthlyTagData = data;
  }

  private getMonthlyTagData(): MonthlyTagData[] {
    return this.monthlyTagData;
  }

  get abstinenceDescription() {
    return this.formAbstinence.get("description");
  }

  get vacationDescription() {
    return this.formVacation.get("description");
  }

  get appointmentDescription() {
    return this.formAppointment.get("description");
  }

  get appointmentJustification() {
    return this.formAppointment.get("justification");
  }

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private toast: ToastrService,
    private taskService: TasksService,
    private tagService: TagService, // TODO: retirar busca por tags - deixar hard coded
    private appointmentService: AppointmentService,
    private personService: PersonService,
    public router: Router,
    private datePipe: DatePipe,
    private authService: AuthService,
    private chatService: ChatService,
    private logger: LoggerService,
    // Novos services
    private calendarService: CalendarService,
    private modalService: ModalService,
    private appointmentStateService: AppointmentStateService,
    private tagManagementService: TagManagementService,
    private validationService: ValidationService,
    private errorHandlerService: ErrorHandlerService,
    private cacheService: CacheService,
    private goalsService: GoalsService,
    private cdr: ChangeDetectorRef,

    private abstinenceService: AbstinenceService,
  ) {}

  /**
   * Calcula o total de avaliações dos últimos 6 meses
   */
  calculateTotalEvaluationsLast6Months(trackInit = false): void {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    sixMonthsAgo.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    this.withInitTracking(
      this.appointmentService.findByPersonAndDate(this.personId, sixMonthsAgo, today),
      trackInit,
    ).subscribe({
      next: (appointments: Appointment[]) => {
        this.totalEvaluationsLast6Months = appointments ? appointments.length : 0;
        this.logger.debug("Total evaluations last 6 months:", this.totalEvaluationsLast6Months);
      },
      error: (error) => {
        this.logger.error("Error calculating total evaluations last 6 months:", error);
        this.totalEvaluationsLast6Months = 0;
      },
    });
  }

  /**
   * Calcula o total de avaliações do mês atual
   */
  calculateEvaluationsThisMonth(trackInit = false): void {
    const firstDayOfMonth = new Date();
    firstDayOfMonth.setDate(1);
    firstDayOfMonth.setHours(0, 0, 0, 0);

    const lastDayOfMonth = new Date();
    lastDayOfMonth.setMonth(lastDayOfMonth.getMonth() + 1, 0);
    lastDayOfMonth.setHours(23, 59, 59, 999);

    this.withInitTracking(
      this.appointmentService.findByPersonAndDate(this.personId, firstDayOfMonth, lastDayOfMonth),
      trackInit,
    ).subscribe({
      next: (appointments: Appointment[]) => {
        this.evaluationsThisMonth = appointments ? appointments.length : 0;
        this.logger.debug("Evaluations this month:", this.evaluationsThisMonth);
      },
      error: (error) => {
        this.logger.error("Error calculating evaluations this month:", error);
        this.evaluationsThisMonth = 0;
      },
    });
  }

  /**
   * Carrega todas as estatísticas
   */
  loadStatistics(trackInit = false): void {
    this.calculateTotalEvaluationsLast6Months(trackInit);
    this.calculateEvaluationsThisMonth(trackInit);
  }

  ngOnInit(): void {
    this.loadPersonData(true);

    // this.loadMessages(true);

    this.getAppointmentsByToday(true);

    this.getActivities(true);

    this.buildTagsData(true);

    this.buildFormsGroup();

    this.buildAbstinenceList(true);

    this.buildVacationList(true);

    this.loadVacations(true);

    this.loadStatistics(true);
  }

  ngOnDestroy(): void {
    localStorage.setItem("isLoadedBefore", "false");
    localStorage.removeItem("currentMonth");
    localStorage.removeItem("lastMonth");
    localStorage.removeItem("lastTwoMonth");
    localStorage.removeItem("lastThreeMonth");
    localStorage.removeItem("lastFourMonth");
    localStorage.removeItem("lastFiveMonth");
  }

  ngAfterViewInit(): void {
    if (localStorage.getItem("isLoadedBefore") === "false" || !localStorage.getItem("isLoadedBefore")) {
      window.location.reload();
      localStorage.setItem("isLoadedBefore", "true");
    }
  }

  loadVacations(trackInit = false) {
    // this.withInitTracking(this.vacationServiceV2.findAllByPerson(this.personId), trackInit).subscribe({
    //   next: (response) => {
    //     if (response != null) {
    //       this.vacationsV2 = response;
    //       // Converter para o formato do modal
    //       this.vacationsForModal = this.convertVacationsToModalData(response);
    //     }
    //   },
    //   error: (error) => {
    //     // Não exibir erro 404 para férias, pois é normal não ter dados
    //     if (error.status !== 404) {
    //       this.errorHandlerService.handle(error);
    //     }
    //   },
    // });
  }

  // loadMessages(trackInit = false) {
  //   if (this.loading) return;
  //   this.loading = true;

  //   const request$ = this.chatService
  //     .getConversation(this.userA, this.userB, this.page, this.size)
  //     .pipe(finalize(() => (this.loading = false)));

  //   this.withInitTracking(request$, trackInit).subscribe((pageData) => {
  //     this.messages = [...pageData.content, ...this.messages];
  //     this.page++;
  //   });
  // }

  // Chamado, por exemplo, ao rolar a lista para o topo (scroll infinito)
  // onScrollTop() {
  //   this.loadMessages();
  // }

  buildFormsGroup() {
    this.formAppointment = new FormGroup({
      id: new FormControl(""),
      personId: new FormControl(""),
      description: new FormControl("", [Validators.required, Validators.maxLength(this.maxLength)]),
      justification: new FormControl("", [Validators.maxLength(this.maxLength)]),
      activityType: new FormControl(""),
      tagId: new FormControl(""),
      activityId: new FormControl(""),
      createdAt: new FormControl(""),
    });
    this.formAbstinence = new FormGroup({
      id: new FormControl(""),
      personId: new FormControl(""),
      description: new FormControl("", Validators.maxLength(this.maxLength)),
      document: new FormControl(null, Validators.required),
      startDate: new FormControl("", Validators.required),
      endDate: new FormControl("", Validators.required),
    });
    this.formVacation = new FormGroup({
      id: new FormControl(""),
      personId: new FormControl(""),
      description: new FormControl("", Validators.maxLength(this.maxLength)),
      startDate: new FormControl("", Validators.required),
      endDate: new FormControl("", Validators.required),
    });
    this.chatForm = new FormGroup({
      message: new FormControl("", Validators.required),
    });
    this.formReport = new FormGroup({
      startDate: new FormControl("", Validators.required),
      endDate: new FormControl("", Validators.required),
    });
  }

  loadPersonData(trackInit = false) {
    this.personId = this.route.snapshot.params["id"];
    this.loadPersonById(trackInit);
    // Carregar listas auxiliares
    this.loadTasksForPerson(trackInit);
    this.loadGoalsForPerson(trackInit);
  }

  buildTagsData(trackInit = false) {
    this.buildTagList(trackInit);

    this.getDateCalendar();

    this.getCurrentMonthTags();

    this.getLastMonthTags();

    this.getLastTwoMonthTags();

    this.getLastThreeMonthTags();

    this.getLastFourMonthTags();

    this.getLastFiveMonthTags();
  }

  buildAbstinenceList(trackInit = false): void {
    this.withInitTracking(this.appointmentService.findAbstinencesByPerson(this.personId), trackInit).subscribe({
      next: (response) => {
        if (response != null) {
          this.abstinences = response;
        }
      },
      error: (error) => {
        // Não exibir erro 404 para atestados, pois é normal não ter dados
        if (error.status !== 404) {
          this.errorHandlerService.handle(error);
        }
      },
    });
  }

  buildVacationList(trackInit = false) {
    // TODO: alterar para findAllVacation V2

    this.withInitTracking(this.appointmentService.findVacationsByPerson(this.personId), trackInit).subscribe({
      next: (response) => {
        if (response != null) {
          this.vacations = response;
        }
      },
      error: (error) => {
        // Não exibir erro 404 para férias, pois é normal não ter dados
        if (error.status !== 404) {
          this.errorHandlerService.handle(error);
        }
      },
    });
  }

  loadPersonById(trackInit = false): void {
    this.withInitTracking(this.personService.findById(this.personId), trackInit).subscribe({
      next: (person) => {
        this.person = person;
      },
      error: (error) => {
        this.errorHandlerService.handle(error);
      },
    });
  }

  buildTagList(trackInit = false): void {
    this.withInitTracking(this.tagService.findAll(), trackInit).subscribe((response) => {
      // Usar o TagManagementService para processar as tags
      const filteredTags = this.tagManagementService.filterTags(response);
      this.tags = this.tagManagementService.processTags(filteredTags);

      // Aplicar configurações de tags usando constantes
      this.tags.forEach((tag) => {
        const tagConfig = TAG_CONFIG.DESCRIPTIONS[tag.name as keyof typeof TAG_CONFIG.DESCRIPTIONS];
        const colorConfig = TAG_CONFIG.COLORS[tag.name as keyof typeof TAG_CONFIG.COLORS];

        if (tagConfig) {
          tag.description = tagConfig;
        }
        if (colorConfig) {
          tag.class = colorConfig;
        }
      });

      // Atualizar estado do componente usando interface
      this.updateComponentState({ tags: this.tags });

      // Após carregar tags, recarregar avaliações do dia para associá-las às listas
      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
      const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
      this.loadDailyAppointmentsForLists(startDate, endDate, trackInit);
    });
  }

  getActivities(trackInit = false): void {
    const newStartDate = new Date(
      this.startDate.getFullYear(),
      this.startDate.getMonth(),
      this.startDate.getDate(),
      0,
      0,
      0,
      0,
    );
    const newEndDate = new Date(
      this.startDate.getFullYear(),
      this.startDate.getMonth(),
      this.startDate.getDate(),
      23,
      59,
      59,
      999,
    );

    // Atualizar filtros usando interface
    this.updateFilterData({
      dateRange: { start: newStartDate, end: newEndDate },
      personId: this.personId,
    });

    // Criar chave de cache única
    const cacheKey = `activities_${this.personId}_${newStartDate.toISOString().split("T")[0]}`;

    const activities$ = this.cacheService.get<Activity[]>(
      cacheKey,
      () => this.appointmentService.findActivitiesByPersonAndDate(this.personId, newStartDate, newEndDate),
      { ttl: 5 * 60 * 1000 },
    );

    this.withInitTracking(activities$, trackInit).subscribe((response) => {
      this.activitiesDailyResponse = response;
      this.dataSource = response;
      this.updateComponentState({ activities: response });
      this.activitiesResponse = response;
      this.loadDailyAppointmentsForLists(newStartDate, newEndDate, trackInit);
    });
  }

  // Carrega avaliações do dia e associa às listas (Rotinas/Tarefas/Metas)
  private loadDailyAppointmentsForLists(startDate: Date, endDate: Date, trackInit = false): void {
    // Se as tags ainda não foram carregadas, não faz nada (será chamado novamente após tags carregarem)
    if (!this.tags || this.tags.length === 0) {
      this.logger.debug("Tags ainda não carregadas, aguardando...");
      return;
    }

    this.withInitTracking(
      this.appointmentService.findByPersonAndDate(this.personId, startDate, endDate),
      trackInit,
    ).subscribe({
      next: (appointments: Appointment[]) => {
        if (!appointments || appointments.length === 0) {
          // Se não há avaliações, limpar tags das listas
          if (this.activitiesDailyResponse) {
            this.activitiesDailyResponse = this.activitiesDailyResponse.map(
              (a) => ({ ...a, tag: undefined, tagId: "" }) as Activity,
            );
          }
          if (this.tasksForList) {
            this.tasksForList = this.tasksForList.map((t) => ({ ...t, tag: undefined, tagId: "" }) as Activity);
          }
          if (this.goalsForList) {
            this.goalsForList = this.goalsForList.map((g) => ({ ...g, tag: undefined, tagId: "" }) as Activity);
          }
          return;
        }

        // Mapear appointments por activityId e routineId (normalizar IDs para string para comparação)
        // Separar por tipo de atividade para melhor associação
        const appointmentsMapByActivityId = new Map<string, Appointment>();
        const appointmentsMapByRoutineId = new Map<string, Appointment>();
        const taskAppointments = new Map<string, Appointment>();
        const goalAppointments = new Map<string, Appointment>();
        const routineAppointments = new Map<string, Appointment>();

        appointments.forEach((apt) => {
          const activityIdKey = apt.activityId ? String(apt.activityId).trim() : null;
          const routineIdKey = apt.routineId ? String(apt.routineId).trim() : null;
          const taskIdKey = (apt as any).taskId ? String((apt as any).taskId).trim() : null;
          const goalIdKey = (apt as any).goalId ? String((apt as any).goalId).trim() : null;

          if (activityIdKey) {
            appointmentsMapByActivityId.set(activityIdKey, apt);
          }
          if (routineIdKey) {
            appointmentsMapByRoutineId.set(routineIdKey, apt);
            routineAppointments.set(routineIdKey, apt);
          }
          if (taskIdKey) {
            taskAppointments.set(taskIdKey, apt);
          }
          if (goalIdKey) {
            goalAppointments.set(goalIdKey, apt);
          }

          // Fallback por tipo textual
          const activityType = (apt.activityType || "").toString().toLowerCase();
          if (activityIdKey) {
            if (activityType === "task") {
              taskAppointments.set(activityIdKey, apt);
            } else if (activityType === "assignment" || activityType === "goal") {
              goalAppointments.set(activityIdKey, apt);
            } else {
              routineAppointments.set(activityIdKey, apt);
            }
          }
        });

        this.logger.debug("=== ASSOCIATING APPOINTMENTS TO LISTS ===");
        this.logger.debug("Appointments found:", appointments.length);
        this.logger.debug("Task appointments:", Array.from(taskAppointments.keys()));
        this.logger.debug("Goal appointments:", Array.from(goalAppointments.keys()));
        this.logger.debug("Routine appointments:", Array.from(routineAppointments.keys()));

        // Associar tags às rotinas (verificar tanto activityId quanto routineId)
        if (this.activitiesDailyResponse && this.activitiesDailyResponse.length > 0) {
          this.activitiesDailyResponse = this.activitiesDailyResponse.map((activity) => {
            const activityIdStr = String(activity.id || "").trim();
            // Tentar primeiro por activityId, depois por routineId (para rotinas)
            let apt = appointmentsMapByActivityId.get(activityIdStr) || appointmentsMapByRoutineId.get(activityIdStr);

            if (apt && apt.tagId) {
              const tag = this.tags.find((t) => String(t.id).trim() === String(apt.tagId).trim());
              if (tag) {
                this.logger.debug(`Matched appointment for routine ${activity.name} with tag ${tag.name}`);
                // Incluir descrição e justificativa do appointment
                return {
                  ...activity,
                  tag,
                  tagId: apt.tagId,
                  description: apt.description || activity.description || "",
                  justification: apt.justification || activity.justification || "",
                } as Activity;
              } else {
                this.logger.debug(`Tag not found for appointment: tagId=${apt.tagId}`);
              }
            } else {
              this.logger.debug(`No appointment found for routine ${activity.name} (id: ${activityIdStr})`);
            }
            return { ...activity, tag: undefined, tagId: "" } as Activity;
          });

          // Forçar detecção de mudança criando novo array
          this.activitiesDailyResponse = [...this.activitiesDailyResponse];
        }

        // Associar tags às tarefas (filtrar apenas appointments de tipo Task)
        if (this.tasksForList && this.tasksForList.length > 0) {
          this.logger.debug(`Processing ${this.tasksForList.length} tasks`);

          this.tasksForList = this.tasksForList.map((task) => {
            const taskIdStr = String(task.id || "").trim();

            // Tentar primeiro nos appointments de tarefas, depois no mapa geral
            let apt = taskAppointments.get(taskIdStr) || appointmentsMapByActivityId.get(taskIdStr);

            if (apt && apt.tagId) {
              const tag = this.tags.find((t) => String(t.id).trim() === String(apt.tagId).trim());
              if (tag) {
                this.logger.debug(`Matched appointment for task ${task.name} (id: ${taskIdStr}) with tag ${tag.name}`);
                // Incluir descrição e justificativa do appointment
                return {
                  ...task,
                  tag,
                  tagId: apt.tagId,
                  description: apt.description || task.description || "",
                  justification: apt.justification || task.justification || "",
                } as Activity;
              } else {
                this.logger.debug(`Tag not found for task appointment: tagId=${apt.tagId}`);
              }
            } else {
              this.logger.debug(`No appointment found for task ${task.name} (id: ${taskIdStr})`);
            }
            return { ...task, tag: undefined, tagId: "" } as Activity;
          });

          // Forçar detecção de mudança
          this.tasksForList = [...this.tasksForList];
        }

        // Associar tags às metas (filtrar apenas appointments de tipo Assignment/Goal)
        if (this.goalsForList && this.goalsForList.length > 0) {
          this.logger.debug(`Processing ${this.goalsForList.length} goals`);

          this.goalsForList = this.goalsForList.map((goal) => {
            const goalIdStr = String(goal.id || "").trim();

            // Tentar primeiro nos appointments de metas, depois no mapa geral
            let apt = goalAppointments.get(goalIdStr) || appointmentsMapByActivityId.get(goalIdStr);

            if (apt && apt.tagId) {
              const tag = this.tags.find((t) => String(t.id).trim() === String(apt.tagId).trim());
              if (tag) {
                this.logger.debug(`Matched appointment for goal ${goal.name} (id: ${goalIdStr}) with tag ${tag.name}`);
                // Incluir descrição e justificativa do appointment
                return {
                  ...goal,
                  tag,
                  tagId: apt.tagId,
                  description: apt.description || goal.description || "",
                  justification: apt.justification || goal.justification || "",
                } as Activity;
              } else {
                this.logger.debug(`Tag not found for goal appointment: tagId=${apt.tagId}`);
              }
            } else {
              this.logger.debug(`No appointment found for goal ${goal.name} (id: ${goalIdStr})`);
            }
            return { ...goal, tag: undefined, tagId: "" } as Activity;
          });

          // Forçar detecção de mudança
          this.goalsForList = [...this.goalsForList];
        }
      },
      error: (error) => this.logger.error("Erro ao buscar avaliações do dia:", error),
    });
  }

  getAppointmentsByToday(trackInit = false): void {
    const hoje = new Date();
    const inicioDoDia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 0, 0, 0, 0);
    const fimDoDia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 59, 999);

    this.withInitTracking(
      this.appointmentService.findByPersonAndDate(this.personId, inicioDoDia, fimDoDia),
      trackInit,
    ).subscribe((response: Appointment[]) => {
      if (response != null) {
        this.appointments = response;
        if (this.appointments && this.appointments.length > 0) {
          this.appointments.forEach((appointment) => {
            this.logger.debug("appointment", appointment);
            this.logger.debug("this.activitiesResponse", this.activitiesResponse);
            if (this.activitiesResponse != undefined) {
              this.activitiesResponse.forEach((activity) => {
                this.logger.debug("activity", activity);
                if (activity.id === appointment.routineId) {
                  activity.description = appointment.description;
                  activity.justification = appointment.justification;
                }
              });
            }
          });
        }
      }
    });
  }

  getCurrentMonthTags(): void {
    this.currentMonthTags = JSON.parse(localStorage.getItem("currentMonth"));
    if (this.currentMonthTags != undefined) {
      const [todayDaySplit, monthSplit, yearSplit] = this.today.split("/");

      // Converter para interface MonthlyTagData
      const monthlyTagData: MonthlyTagData[] = this.currentMonthTags.map((month) => {
        const [monthDateSplit, yearDateSplit, dayDateSplit] = month.date.split("-");
        let tagClass = month.tag;

        if (dayDateSplit == todayDaySplit) {
          tagClass = "Green-Light";
        }

        let newDate = new Date(month.date);
        newDate.setDate(newDate.getDate() + 1);
        this.daysOfMonth.push(newDate.getDate());

        return {
          tag: tagClass,
          description: (month as any).description || "",
          class: tagClass,
        };
      });

      // Atualizar dados de tags mensais usando interface
      this.updateMonthlyTagData(monthlyTagData);
    }
  }

  getLastMonthTags(): void {
    this.lastMonthTags = JSON.parse(localStorage.getItem("lastMonth"));
    if (this.lastMonthTags != undefined) {
      // Converter para interface MonthlyTagData
      const monthlyTagData: MonthlyTagData[] = this.lastMonthTags.map((month) => {
        let newDate = new Date(month.date);
        newDate.setDate(newDate.getDate() + 1);
        this.daysOfLastMonth.push(newDate.getDate());

        return {
          tag: month.tag,
          description: (month as any).description || "",
          class: month.tag,
        };
      });

      // Atualizar dados de tags mensais usando interface
      this.updateMonthlyTagData(monthlyTagData);
    }
  }

  getLastTwoMonthTags(): void {
    this.lastTwoMonthTags = JSON.parse(localStorage.getItem("lastTwoMonth"));
    if (this.lastTwoMonthTags != undefined) {
      // Converter para interface MonthlyTagData
      const monthlyTagData: MonthlyTagData[] = this.lastTwoMonthTags.map((month) => {
        let newDate = new Date(month.date);
        newDate.setDate(newDate.getDate() + 1);
        this.daysOfLastTwoMonth.push(newDate.getDate());

        return {
          tag: month.tag,
          description: (month as any).description || "",
          class: month.tag,
        };
      });

      // Atualizar dados de tags mensais usando interface
      this.updateMonthlyTagData(monthlyTagData);
    }
  }

  getLastThreeMonthTags(): void {
    this.lastThreeMonthTags = JSON.parse(localStorage.getItem("lastThreeMonth"));
    if (this.lastThreeMonthTags != undefined) {
      // Converter para interface MonthlyTagData
      const monthlyTagData: MonthlyTagData[] = this.lastThreeMonthTags.map((month) => {
        let newDate = new Date(month.date);
        newDate.setDate(newDate.getDate() + 1);
        this.daysOfLastThreeMonth.push(newDate.getDate());

        return {
          tag: month.tag,
          description: (month as any).description || "",
          class: month.tag,
        };
      });

      // Atualizar dados de tags mensais usando interface
      this.updateMonthlyTagData(monthlyTagData);
    }
  }

  getLastFourMonthTags(): void {
    this.lastFourMonthTags = JSON.parse(localStorage.getItem("lastFourMonth"));
    if (this.lastFourMonthTags != undefined) {
      // Converter para interface MonthlyTagData
      const monthlyTagData: MonthlyTagData[] = this.lastFourMonthTags.map((month) => {
        let newDate = new Date(month.date);
        newDate.setDate(newDate.getDate() + 1);
        this.daysOfLastFourMonth.push(newDate.getDate());

        return {
          tag: month.tag,
          description: (month as any).description || "",
          class: month.tag,
        };
      });

      // Atualizar dados de tags mensais usando interface
      this.updateMonthlyTagData(monthlyTagData);
    }
  }

  getLastFiveMonthTags(): void {
    this.lastFiveMonthTags = JSON.parse(localStorage.getItem("lastFiveMonth"));
    if (this.lastFiveMonthTags != undefined) {
      // Converter para interface MonthlyTagData
      const monthlyTagData: MonthlyTagData[] = this.lastFiveMonthTags.map((month) => {
        let newDate = new Date(month.date);
        newDate.setDate(newDate.getDate() + 1);
        this.daysOfLastFiveMonth.push(newDate.getDate());

        return {
          tag: month.tag,
          description: (month as any).description || "",
          class: month.tag,
        };
      });

      // Atualizar dados de tags mensais usando interface
      this.updateMonthlyTagData(monthlyTagData);
    }
  }

  getDateCalendar() {
    // Usar o CalendarService para gerar as datas com configurações
    const monthRanges = this.calendarService.getMultipleMonthRanges([0, -1, -2, -3, -4, -5]);

    // Aplicar configurações de calendário usando constantes
    const currentMonth = monthRanges.get(0)!;
    this.firstDay = currentMonth.firstDay;
    this.lastDay = currentMonth.lastDay;

    const lastMonth = monthRanges.get(-1)!;
    this.firstDayLastMonth = lastMonth.firstDay;
    this.lastDayLastMonth = lastMonth.lastDay;

    const lastTwoMonth = monthRanges.get(-2)!;
    this.firstDayLastTwoMonth = lastTwoMonth.firstDay;
    this.lastDayLastTwoMonth = lastTwoMonth.lastDay;

    const lastThreeMonth = monthRanges.get(-3)!;
    this.firstDayLastThreeMonth = lastThreeMonth.firstDay;
    this.lastDayLastThreeMonth = lastThreeMonth.lastDay;

    const lastFourMonth = monthRanges.get(-4)!;
    this.firstDayLastFourMonth = lastFourMonth.firstDay;
    this.lastDayLastFourMonth = lastFourMonth.lastDay;

    const lastFiveMonth = monthRanges.get(-5)!;
    this.firstDayLastFiveMonth = lastFiveMonth.firstDay;
    this.lastDayLastFiveMonth = lastFiveMonth.lastDay;

    // Aplicar configurações de calendário
    if (CALENDAR_CONFIG.ENABLE_PAST_DATES) {
      // Configurações para datas passadas
    }
    if (CALENDAR_CONFIG.ENABLE_FUTURE_DATES) {
      // Configurações para datas futuras
    }
  }

  // Método removido - agora é feito pelo TagManagementService

  dateClassCurrentMonth: MatCalendarCellClassFunction<Date> = (cellDate, _) => {
    return this.calendarService.createDateClassFunction(this.currentMonthTags, this.daysOfMonth)(cellDate, _);
  };

  dateClassLastMonth: MatCalendarCellClassFunction<Date> = (cellDate, _) => {
    return this.calendarService.createDateClassFunction(this.lastMonthTags, this.daysOfLastMonth)(cellDate, _);
  };

  dateClassLastTwoMonth: MatCalendarCellClassFunction<Date> = (cellDate, _) => {
    return this.calendarService.createDateClassFunction(this.lastTwoMonthTags, this.daysOfLastTwoMonth)(cellDate, _);
  };

  dateClassLastThreeMonth: MatCalendarCellClassFunction<Date> = (cellDate, _) => {
    return this.calendarService.createDateClassFunction(this.lastThreeMonthTags, this.daysOfLastThreeMonth)(
      cellDate,
      _,
    );
  };

  dateClassLastFourMonth: MatCalendarCellClassFunction<Date> = (cellDate, _) => {
    return this.calendarService.createDateClassFunction(this.lastFourMonthTags, this.daysOfLastFourMonth)(cellDate, _);
  };

  dateClassLastFiveMonth: MatCalendarCellClassFunction<Date> = (cellDate, _) => {
    return this.calendarService.createDateClassFunction(this.lastFiveMonthTags, this.daysOfLastFiveMonth)(cellDate, _);
  };

  openDialog() {
    this.selectedDateMonthly = this.selected;

    const startSelectedDateMonthly = new Date(
      this.selectedDateMonthly.getFullYear(),
      this.selectedDateMonthly.getMonth(),
      this.selectedDateMonthly.getDate(),
      0,
      0,
      0,
      0,
    );
    const endSelectedDateMonthly = new Date(
      this.selectedDateMonthly.getFullYear(),
      this.selectedDateMonthly.getMonth(),
      this.selectedDateMonthly.getDate(),
      23,
      59,
      59,
      999,
    );

    this.appointmentService
      .findActivitiesByPersonAndDate(this.personId, startSelectedDateMonthly, endSelectedDateMonthly)
      .subscribe((response) => {
        this.activitiesResponse = response;

        this.dialog.open(PersonAppointmentDialogComponent, {
          data: {
            activities: this.activitiesResponse,
            tags: this.tags,
            personId: this.personId,
            selected: this.selected,
          },
          height: "fit-content",
          width: "fit-content",
        });
      });
  }

  openConfirm(tagId: string, activity: Activity) {
    this.dialog.open(PersonAppointmentConfirmComponent, {
      data: {
        tagId: tagId,
        activityId: activity.id,
        personId: this.personId,
        activityType: activity.type,
        activity: activity,
        selected: new Date(),
      },
      height: "fit-content",
      width: "fit-content",
    });
  }

  openAppointmentModal() {
    // Validar data futura usando constantes
    if (this.selected && this.selected > new Date()) {
      this.toast.error(ERROR_MESSAGES.VALIDATION, "Não se pode salvar avaliação para data futura.");
      return;
    }

    if (!this.selected) {
      this.toast.error(ERROR_MESSAGES.VALIDATION, "Nenhuma data foi selecionada.");
      return;
    }

    // Resetar seleção do modal para Rotinas por padrão
    this.selectedModalListType = "ROTINAS";

    // Definir selectedDateMonthly com a data selecionada no calendário
    // Isso garante que ao salvar a avaliação, use a data correta selecionada
    this.selectedDateMonthly = this.selected;

    // Usar o AppointmentStateService para gerenciar o estado
    this.appointmentStateService.setSelectedDate(this.selected);
    this.appointmentStateService.setSelectedDateForModal(this.datePipe.transform(this.selected, "dd/MM/yyyy") || "");

    const startSelectedDateMonthly = new Date(
      this.selected.getFullYear(),
      this.selected.getMonth(),
      this.selected.getDate(),
      0,
      0,
      0,
      0,
    );
    const endSelectedDateMonthly = new Date(
      this.selected.getFullYear(),
      this.selected.getMonth(),
      this.selected.getDate(),
      23,
      59,
      59,
      999,
    );

    // Carregar appointments e atividades
    this.appointmentService
      .findByPersonAndDate(this.personId, startSelectedDateMonthly, endSelectedDateMonthly)
      .subscribe((appointmentsResponse: Appointment[]) => {
        this.appointmentStateService.setAppointments(appointmentsResponse || []);
        // Associar avaliações às listas do modal após carregar
        this.loadModalAppointmentsForLists(startSelectedDateMonthly, endSelectedDateMonthly);
      });

    // Criar chave de cache única para modal
    const modalCacheKey = `activities_modal_${this.personId}_${startSelectedDateMonthly.toISOString().split("T")[0]}`;

    // Carregar rotinas
    this.cacheService
      .get<Activity[]>(
        modalCacheKey,
        () =>
          this.appointmentService.findActivitiesByPersonAndDate(
            this.personId,
            startSelectedDateMonthly,
            endSelectedDateMonthly,
          ),
        { ttl: 3 * 60 * 1000 },
      )
      .subscribe((response) => {
        this.appointmentStateService.setActivities(response);
        // Carregar tarefas e metas para o modal
        this.loadTasksForModal(startSelectedDateMonthly, endSelectedDateMonthly);
        this.loadGoalsForModal(startSelectedDateMonthly, endSelectedDateMonthly);
        this.modalService.showModal("appointmentsModal");
      });
  }

  // Carrega tarefas para o modal de calendário
  private loadTasksForModal(startDate: Date, endDate: Date): void {
    this.taskService.findAllByPersonId(this.personId).subscribe({
      next: (tasks: any[]) => {
        this.tasksForModal = (tasks || []).map((t) => ({
          id: t.id?.toString?.() || t.id,
          name: t.title || t.name,
          type: 0,
          description: "",
          justification: "",
          tag: undefined as unknown as Tag,
          tagId: "",
          createdAt: t.createdAt || "",
          appointmentId: "",
        }));

        // Associar avaliações às tarefas do modal
        this.loadModalAppointmentsForLists(startDate, endDate);
      },
      error: () => {
        this.tasksForModal = [];
      },
    });
  }

  // Carrega metas para o modal de calendário
  private loadGoalsForModal(startDate: Date, endDate: Date): void {
    this.goalsService.findAllByPersonId(this.personId).subscribe({
      next: (goals: any[]) => {
        const filtered = (goals || []).filter((g) => {
          const ownerId = g?.person?.id || g?.personId;
          const persons = g?.persons || [];
          return ownerId === this.personId || persons.includes?.(this.personId);
        });
        this.goalsForModal = filtered.map((g) => ({
          id: g.id?.toString?.() || g.id,
          name: g.name || g.title,
          type: 0,
          description: "",
          justification: "",
          tag: undefined as unknown as Tag,
          tagId: "",
          createdAt: g.createdAt || "",
          appointmentId: "",
        }));

        // Associar avaliações às metas do modal
        this.loadModalAppointmentsForLists(startDate, endDate);
      },
      error: () => {
        this.goalsForModal = [];
      },
    });
  }

  // Carrega avaliações do dia e associa às listas do modal (Rotinas/Tarefas/Metas)
  private loadModalAppointmentsForLists(startDate: Date, endDate: Date): void {
    // Se as tags ainda não foram carregadas, não faz nada
    if (!this.tags || this.tags.length === 0) {
      this.logger.debug("Tags ainda não carregadas, aguardando...");
      return;
    }

    const appointments = this.appointmentStateService.currentState.appointments;
    if (!appointments || appointments.length === 0) {
      // Se não há avaliações, limpar tags das listas do modal
      if (this.appointmentStateService.currentState.activities) {
        const clearedActivities = this.appointmentStateService.currentState.activities.map(
          (a) => ({ ...a, tag: undefined, tagId: "" }) as Activity,
        );
        this.appointmentStateService.setActivities(clearedActivities);
      }
      if (this.tasksForModal) {
        this.tasksForModal = this.tasksForModal.map((t) => ({ ...t, tag: undefined, tagId: "" }) as Activity);
      }
      if (this.goalsForModal) {
        this.goalsForModal = this.goalsForModal.map((g) => ({ ...g, tag: undefined, tagId: "" }) as Activity);
      }
      return;
    }

    // Mapear appointments por activityId, taskId, goalId e routineId
    const appointmentsMapByActivityId = new Map<string, Appointment>();
    const appointmentsMapByRoutineId = new Map<string, Appointment>();
    const taskAppointments = new Map<string, Appointment>();
    const goalAppointments = new Map<string, Appointment>();

    appointments.forEach((apt) => {
      const activityIdKey = apt.activityId ? String(apt.activityId).trim() : null;
      const routineIdKey = apt.routineId ? String(apt.routineId).trim() : null;
      const taskIdKey = (apt as any).taskId ? String((apt as any).taskId).trim() : null;
      const goalIdKey = (apt as any).goalId ? String((apt as any).goalId).trim() : null;

      if (activityIdKey) {
        appointmentsMapByActivityId.set(activityIdKey, apt);

        const activityType = (apt.activityType || "").toString().toLowerCase();
        if (activityType === "task") {
          taskAppointments.set(activityIdKey, apt);
        } else if (activityType === "assignment" || activityType === "goal" || activityType === "goal") {
          goalAppointments.set(activityIdKey, apt);
        }
      }

      if (routineIdKey) {
        appointmentsMapByRoutineId.set(routineIdKey, apt);
      }
      if (taskIdKey) {
        taskAppointments.set(taskIdKey, apt);
      }
      if (goalIdKey) {
        goalAppointments.set(goalIdKey, apt);
      }
    });

    // Associar tags às rotinas do modal
    const currentActivities = this.appointmentStateService.currentState.activities || [];
    if (currentActivities.length > 0) {
      const updatedActivities = currentActivities.map((activity) => {
        const activityIdStr = String(activity.id || "").trim();
        let apt = appointmentsMapByActivityId.get(activityIdStr) || appointmentsMapByRoutineId.get(activityIdStr);

        if (apt && apt.tagId) {
          const tag = this.tags.find((t) => String(t.id).trim() === String(apt.tagId).trim());
          if (tag) {
            return { ...activity, tag, tagId: apt.tagId } as Activity;
          }
        }
        return { ...activity, tag: undefined, tagId: "" } as Activity;
      });
      this.appointmentStateService.setActivities(updatedActivities);
    }

    // Associar tags às tarefas do modal
    if (this.tasksForModal && this.tasksForModal.length > 0) {
      this.tasksForModal = this.tasksForModal.map((task) => {
        const taskIdStr = String(task.id || "").trim();
        let apt = taskAppointments.get(taskIdStr) || appointmentsMapByActivityId.get(taskIdStr);

        if (apt && apt.tagId) {
          const tag = this.tags.find((t) => String(t.id).trim() === String(apt.tagId).trim());
          if (tag) {
            return { ...task, tag, tagId: apt.tagId } as Activity;
          }
        }
        return { ...task, tag: undefined, tagId: "" } as Activity;
      });
    }

    // Associar tags às metas do modal
    if (this.goalsForModal && this.goalsForModal.length > 0) {
      this.goalsForModal = this.goalsForModal.map((goal) => {
        const goalIdStr = String(goal.id || "").trim();
        let apt = goalAppointments.get(goalIdStr) || appointmentsMapByActivityId.get(goalIdStr);

        if (apt && apt.tagId) {
          const tag = this.tags.find((t) => String(t.id).trim() === String(apt.tagId).trim());
          if (tag) {
            return { ...goal, tag, tagId: apt.tagId } as Activity;
          }
        }
        return { ...goal, tag: undefined, tagId: "" } as Activity;
      });
    }
  }

  // Permite trocar o tipo de lista no modal a partir do template
  onChangeModalListType(type: "ROTINAS" | "TAREFAS" | "METAS"): void {
    this.selectedModalListType = type;
  }

  openAppointmentCreateModal(activity: Activity, tag: Tag): void {
    const currentUserId = this.authService.getCurrentUserId();
    if (activity.reviewerId != null && activity.reviewerId != currentUserId) {
      this.toast.error("Você não pode abrir uma Avaliacão de outra pessoa.");
      return;
    }

    // Guardar contexto selecionado para atualização posterior
    this.lastSelectedActivityId = activity.id;
    this.lastSelectedTag = tag;

    // Limpar formulário antes de preenchê-lo
    this.formAppointment.reset();

    // Determinar a data selecionada: priorizar selectedDateMonthly (modal mensal), senão usar hoje (aba Diária)
    const today = new Date();
    let dateSelected = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    let isDailyTab = false;

    if (this.selectedDateMonthly != undefined) {
      // Vem do modal mensal (calendário)
      dateSelected = new Date(
        this.selectedDateMonthly.getFullYear(),
        this.selectedDateMonthly.getMonth(),
        this.selectedDateMonthly.getDate(),
        0,
        0,
        0,
        0,
      );
    } else {
      // Aba Diária - sempre usar hoje
      dateSelected = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
      isDailyTab = true;
    }

    const endDateSelected = new Date(dateSelected);
    endDateSelected.setHours(23, 59, 59, 999);

    // Buscar avaliações apenas da data específica (não usar cache/estado de outras datas)
    this.appointmentService.findByPersonAndDate(this.personId, dateSelected, endDateSelected).subscribe({
      next: (appointments: Appointment[]) => {
        // Buscar avaliação considerando activityId, taskId, goalId e routineId
        const activityIdStr = String(activity.id || "").trim();
        const existingAppointment = appointments?.find((apt) => {
          const aptActivityId = apt.activityId ? String(apt.activityId).trim() : "";
          const aptTaskId = (apt as any).taskId ? String((apt as any).taskId).trim() : "";
          const aptGoalId = (apt as any).goalId ? String((apt as any).goalId).trim() : "";
          const aptRoutineId = apt.routineId ? String(apt.routineId).trim() : "";

          return (
            aptActivityId === activityIdStr ||
            aptTaskId === activityIdStr ||
            aptGoalId === activityIdStr ||
            aptRoutineId === activityIdStr
          );
        });

        // Só definir o ID se realmente existir um appointment para esta data específica
        if (existingAppointment && existingAppointment.id) {
          this.formAppointment.get("id").patchValue(existingAppointment.id);
        } else {
          // Garantir que o ID está vazio para criar um novo appointment
          this.formAppointment.get("id").patchValue("");
        }

        // Usar o AppointmentStateService para definir data
        this.appointmentStateService.setSelectedDateForModal(this.datePipe.transform(dateSelected, "dd/MM/yyyy") || "");
        // Usar o AppointmentStateService para definir nome da atividade
        this.appointmentStateService.setSelectedActivityName(activity.name);

        // Preparar dados para o formulário
        // Determinar se veio do modal de calendário ou da aba Diária
        const isFromModal = this.selectedDateMonthly != undefined;
        const currentListType = isFromModal ? this.selectedModalListType : this.selectedListType;
        const isRoutineContext = currentListType === "ROTINAS";
        let formData = {
          personId: this.personId,
          description: "",
          justification: "",
          activityType: activity.activityType,
          tagId: tag.id,
          activityId: activity.id,
          createdAt: dateSelected,
        };

        // Definir activityType conforme a seleção atual (fallback quando não vier da activity)
        if (!formData.activityType) {
          // Ajuste para valores esperados pelo backend usando o tipo correto (modal ou diária)
          formData.activityType = this.mapSelectedListTypeToActivityType(currentListType) as any;
        }

        // Se existe um appointment nesta data, usar os dados do appointment (prioridade máxima)
        if (existingAppointment && existingAppointment.id) {
          formData.description = existingAppointment.description || "";
          formData.justification = existingAppointment.justification || "";
          this.logger.debug("Using appointment data - description:", formData.description);
        } else {
          // Se não existe appointment mas o activity já tem descrição (da associação anterior com appointment), usar ela
          // Isso acontece quando a lista já foi atualizada com os dados do appointment
          // IMPORTANTE: Só usar se realmente vier de uma avaliação existente (não da tarefa/meta original)
          if (activity.description && activity.tag) {
            // Se tem tag, significa que já foi associado a um appointment, então a descrição é de avaliação
            formData.description = activity.description;
            formData.justification = activity.justification || "";
            this.logger.debug("Using activity description from appointment association:", formData.description);
          } else if (isRoutineContext) {
            // Apenas para rotinas, pode ter descrição sugerida da própria rotina
            formData.description = activity.description || "";
            formData.justification = activity.justification || "";
          }
          // Para Tarefas e Metas sem appointment, descrição fica vazia
        }

        this.formAppointment.patchValue(formData);

        const modalElement = document.getElementById("appointmentsCreateModal");
        if (modalElement) {
          const modalInstance =
            this.modalService.getInstance(modalElement) || this.modalService.createModal(modalElement);
          modalInstance.show();
          this.closeAppointmentsModal();
        }
      },
      error: (error) => {
        this.logger.error("Erro ao buscar avaliações:", error);
        this.toast.error("Erro ao carregar avaliações. Tente novamente.");
      },
    });
  }

  closeAppointmentsModal(): void {
    // Usar o AppointmentStateService para limpar dados
    this.appointmentStateService.setActivities([]);
    this.modalService.hideModal("appointmentsModal");
    // Limpar selectedDateMonthly quando fechar o modal para evitar interferência na aba Diária
    this.selectedDateMonthly = null;
  }

  closeAppointmentCreateModal(): void {
    // Usar o AppointmentStateService para limpar dados
    this.formAppointment.reset();
    this.appointmentStateService.setSelectedDate(null);
    this.appointmentStateService.setSelectedDateForModal("");
    this.appointmentStateService.setSelectedActivityName("");
    this.modalService.hideModal("appointmentsCreateModal");
    // Limpar selectedDateMonthly quando fechar o modal para evitar interferência na aba Diária
    this.selectedDateMonthly = null;
  }

  onSubmitFormAppointment() {
    this.isSavingAppointment = true;

    // Atualizar dados de formulário usando interface
    this.updateAppointmentFormData({
      id: this.formAppointment.get("id")?.value || "",
      personId: this.formAppointment.get("personId")?.value || "",
      activityId: this.formAppointment.get("activityId")?.value || "",
      description: this.formAppointment.get("description")?.value || "",
      justification: this.formAppointment.get("justification")?.value || "",
      createdAt: this.formAppointment.get("createdAt")?.value || new Date(),
    });

    // Validar formulário usando constantes de configuração
    const validations = this.validationService.validateAppointmentForm(this.formAppointment);
    if (validations.some((v) => !v.isValid)) {
      this.errorHandlerService.handleDataValidationError(
        validations.map((v) => v.message),
        {
          component: "EmployeeAppointmentComponent",
          method: "onSubmitFormAppointment",
          operation: "validation",
        },
      );
      this.isSavingAppointment = false;
      return;
    }

    const id = this.formAppointment.get("id").value;

    // Obter a data do formulário ou usar data atual
    const appointmentDate = this.formAppointment.get("createdAt")?.value || new Date();
    const dateToReload = new Date(appointmentDate);
    const startDate = new Date(dateToReload.getFullYear(), dateToReload.getMonth(), dateToReload.getDate(), 0, 0, 0, 0);
    const endDate = new Date(
      dateToReload.getFullYear(),
      dateToReload.getMonth(),
      dateToReload.getDate(),
      23,
      59,
      59,
      999,
    );

    // Verificar se veio do modal de calendário ou da aba Diária
    const isFromModal = this.selectedDateMonthly != undefined;

    // Função para atualizar todas as listas
    const updateAllLists = () => {
      // Recarregar avaliações e atualizar appointments no estado
      this.appointmentService.findByPersonAndDate(this.personId, startDate, endDate).subscribe({
        next: (appointments: Appointment[]) => {
          this.appointmentStateService.setAppointments(appointments || []);

          // Atualizar listas da aba Diária
          this.loadDailyAppointmentsForLists(startDate, endDate);

          // Se veio do modal, atualizar também as listas do modal
          if (isFromModal) {
            this.loadModalAppointmentsForLists(startDate, endDate);
          }
        },
        error: (error) => this.logger.error("Erro ao recarregar avaliações:", error),
      });
    };

    if (id != "") {
      // update - remover type do objeto antes de enviar
      const formValue = { ...this.formAppointment.value };
      delete formValue.type;
      this.appointmentService.createByDate(formValue).subscribe({
        next: () => {
          this.toast.success(SUCCESS_MESSAGES.UPDATED, "Cadastro");
          // Atualizar todas as listas
          updateAllLists();
          this.closeAppointmentCreateModal();
          this.closeAppointmentsModal();
          this.isSavingAppointment = false;
          // Recarregar a página após salvar com sucesso
          setTimeout(() => {
            window.location.reload();
          }, 500);
        },
        error: (error) => {
          this.errorHandlerService.handle(error);
          this.isSavingAppointment = false;
        },
        complete: () => (this.isSavingAppointment = false),
      });
      return;
    }

    // create - remover type do objeto antes de enviar
    const formValue = { ...this.formAppointment.value };
    delete formValue.type;
    this.appointmentService.createByDate(formValue).subscribe({
      next: () => {
        this.toast.success(SUCCESS_MESSAGES.CREATED, "Cadastro");
        // Atualizar todas as listas
        updateAllLists();
        this.closeAppointmentCreateModal();
        this.closeAppointmentsModal();
        // Usar o AppointmentStateService para limpar dados
        this.appointmentStateService.setActivities([]);
        this.isSavingAppointment = false;
        // Recarregar a página após salvar com sucesso
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
      error: (error) => {
        this.errorHandlerService.handle(error);
        this.isSavingAppointment = false;
      },
      complete: () => (this.isSavingAppointment = false),
    });
  }

  onEditAbstinence(abstinence: AbstinenceData) {
    const modalAbstinencesElement = document.getElementById("abstinencesModal");
    if (modalAbstinencesElement) {
      const modalInstance =
        this.modalService.getInstance(modalAbstinencesElement) ||
        this.modalService.createModal(modalAbstinencesElement);
      modalInstance.hide();
    }
    this.formAbstinence.patchValue(abstinence);
    const modalElement = document.getElementById("abstinencesCreateModal");
    if (modalElement) {
      const modal = this.modalService.createModal(modalElement);
      modal.show();
    }
  }

  onDeleteAbstinence(id: string) {
    this.isSavingAbstinence = true;
    this.taskService.delete(id).subscribe({
      next: () => {
        this.toast.success("Atestado Médico deletado com sucesso", "Exclusão");
        setTimeout(() => {
          this.isSavingAbstinence = false;
          window.location.reload();
        }, 1000);
      },
      error: (error) => {
        this.errorHandlerService.handle(error);
        this.isSavingAbstinence = false;
      },
    });
  }

  onEditVacation(vacation: any) {
    // Fechar modal de listagem
    const modalVacationsElement = document.getElementById("vacationsModal");
    if (modalVacationsElement) {
      const modalInstance =
        this.modalService.getInstance(modalVacationsElement) || this.modalService.createModal(modalVacationsElement);
      modalInstance.hide();
    }

    // Preencher formulário com dados da férias para edição
    this.formVacation.patchValue({
      id: vacation.id,
      personId: this.personId,
      description: vacation.notes || vacation.description,
      startDate: vacation.startDate,
      endDate: vacation.endDate,
    });

    // Abrir modal de criação/edição
    const modalElement = document.getElementById("vacationsCreateModal");
    if (modalElement) {
      const modal = this.modalService.createModal(modalElement);
      modal.show();
    }
  }

  onDeleteVacation(id: string) {
    this.isSavingVacation = true;
    this.taskService.delete(id).subscribe({
      next: () => this.toast.success("Férias deletada com sucesso", "Exclusão"),
      error: (error) => this.errorHandlerService.handle(error),
      complete: () => (this.isSavingVacation = false),
    });
  }

  onDeleteVacationV2(vacationId: string | number) {
    this.isSavingVacation = true;
    const id = typeof vacationId === "string" ? parseInt(vacationId, 10) : vacationId;

    // this.vacationServiceV2.deleteVacation(id).subscribe({
    //   next: () => {
    //     this.toast.success("Férias deletada com sucesso", "Exclusão");
    //     setTimeout(() => {
    //       this.isSavingVacation = false;
    //       window.location.reload();
    //     }, 1000);
    //   },
    //   error: (error) => this.errorHandlerService.handle(error),
    //   complete: () => (this.isSavingVacation = false),
    // });
  }

  openAbstinencesModal(): void {
    const modalElement = document.getElementById("abstinencesModal");
    if (modalElement) {
      const modalInstance = this.modalService.getInstance(modalElement) || this.modalService.createModal(modalElement);
      modalInstance.show();
    }
  }

  openAbstinenceCreateModal(): void {
    this.formAbstinence.reset();
    const modalElement = document.getElementById("abstinencesCreateModal");
    if (modalElement) {
      const modalInstance = this.modalService.getInstance(modalElement) || this.modalService.createModal(modalElement);
      modalInstance.show();
    }
  }

  closeAbstinenceCreateModal(): void {
    this.formAbstinence.reset();
    const modalElement = document.getElementById("abstinencesCreateModal");
    if (modalElement) {
      const modalInstance = this.modalService.getInstance(modalElement) || this.modalService.createModal(modalElement);
      modalInstance.hide();
    }
  }

  openVacationsModal(): void {
    const modalElement = document.getElementById("vacationsModal");
    if (modalElement) {
      const modalInstance = this.modalService.getInstance(modalElement) || this.modalService.createModal(modalElement);
      modalInstance.show();
    }
  }

  openVacationCreateModal(): void {
    this.formVacation.reset();
    const modalElement = document.getElementById("vacationsCreateModal");
    if (modalElement) {
      const modalInstance = this.modalService.getInstance(modalElement) || this.modalService.createModal(modalElement);
      modalInstance.show();
    }
  }

  closeVacationCreateModal(): void {
    this.formVacation.reset();
    const modalElement = document.getElementById("vacationsCreateModal");
    if (modalElement) {
      const modalInstance = this.modalService.getInstance(modalElement) || this.modalService.createModal(modalElement);
      modalInstance.hide();
    }
  }

  onSubmitFormVacation(vacationData?: any) {
    this.isSavingVacation = true;

    if (vacationData) {
      // Dados vêm do modal
      this.vacation = vacationData;
      this.vacation.personId = this.personId;
    } else {
      // Dados vêm do formulário local (fallback)
      this.vacation = this.formVacation.value;
      this.vacation.personId = this.personId;
    }

    if (this.vacation.id) {
      // update
      this.updateVacationV2(this.vacation);
      return;
    }

    // create
    this.createVacationV2();
  }

  updateVacation(vacationId: string): void {}

  updateVacationV2(vacation: any): void {
    this.isSavingVacation = true;

    // const request: VacationRequest = {
    //   personId: this.personId,
    //   notes: this.formVacation.get("description")?.value,
    //   startDate: this.formVacation.get("startDate")!.value,
    //   endDate: this.formVacation.get("endDate")!.value,
    // };

    // this.vacationServiceV2.updateVacation(vacation.id, request).subscribe({
    //   next: () => {
    //     this.toast.success("Férias alterada com sucesso", "Cadastro");
    //     setTimeout(() => {
    //       this.isSavingVacation = false;
    //       window.location.reload();
    //     }, 1000);
    //   },
    //   error: (error) => {
    //     this.errorHandlerService.handle(error);
    //     this.isSavingVacation = false;
    //   },
    // });
  }

  createVacation(): void {
    const vacation = {
      personId: this.personId,
      description: this.formVacation.get("description")?.value,
      startDate: this.formVacation.get("startDate")?.value,
      endDate: this.formVacation.get("endDate")?.value,
    };

    this.appointmentService.createVacation(vacation).subscribe({
      next: () => {
        this.toast.success("Férias cadastrada com sucesso", "Cadastro");
        setTimeout(() => {
          this.isSavingVacation = false;
          window.location.reload();
        }, 1000);
      },
      error: (error) => {
        this.errorHandlerService.handle(error);
        this.isSavingVacation = false;
      },
    });
  }

  createVacationV2(): void {
    // const vacation: VacationRequest = {
    //   personId: this.personId,
    //   notes: this.formVacation.get("description")?.value,
    //   startDate: this.formVacation.get("startDate")!.value,
    //   endDate: this.formVacation.get("endDate")!.value,
    // };
    // this.vacationServiceV2.createVacation(vacation).subscribe({
    //   next: () => {
    //     this.toast.success("Férias cadastrada com sucesso", "Cadastro");
    //     setTimeout(() => {
    //       this.isSavingVacation = false;
    //       window.location.reload();
    //     }, 1000);
    //   },
    //   error: (error) => {
    //     this.errorHandlerService.handle(error);
    //     this.isSavingVacation = false;
    //   },
    // });
  }

  formatTodayDates(date1: string, date2: string): { formattedDate1: string; formattedDate2: string } {
    const parsedDate1 = new Date(date1); // "2024-11-23"
    const [day, month, year] = date2.split("/"); // "23/11/2024"
    const parsedDate2 = new Date(`${year}-${month}-${day}`);

    // Formatar ambas as datas para o mesmo formato, como 'dd/MM/yyyy'
    const formattedDate1 = this.datePipe.transform(parsedDate1, "dd/MM/yyyy");
    const formattedDate2 = this.datePipe.transform(parsedDate2, "dd/MM/yyyy");

    return {
      formattedDate1: formattedDate1 || "",
      formattedDate2: formattedDate2 || "",
    };
  }

  onAbstinenceFileSelected(event: any): void {
    const file = event.target.files[0];

    if (file) {
      this.selectedAbstinenceFile = file;
      this.formAbstinence.patchValue({
        document: file,
      });

      const reader = new FileReader();
      reader.onload = (e) => {
        this.imageUrl = e.target?.result;
      };
      reader.readAsDataURL(file);
    }
  }

  onVacationFileSelected(event: any): void {
    const file = event.target.files[0];

    if (file) {
      this.selectedVacationFile = file;
      this.formVacation.patchValue({
        document: file,
      });

      const reader = new FileReader();
      reader.onload = (e) => {
        this.imageUrl = e.target?.result;
      };
      reader.readAsDataURL(file);
    }
  }

  public sendMessage() {
    if (this.chatForm.valid) {
      const msg = this.chatForm.value.message;
      this.logger.debug("Mensagem enviada:", msg);

      // Aqui você pode chamar um serviço ou adicionar à lista de mensagens

      this.chatForm.reset(); // limpa o input
    }
  }

  // Carrega tarefas do colaborador e mapeia para Activity para reutilizar o template
  private loadTasksForPerson(trackInit = false): void {
    this.withInitTracking(this.taskService.findAllByPersonId(this.personId), trackInit).subscribe({
      next: (tasks: any[]) => {
        this.tasksForList = (tasks || []).map((t) => ({
          id: t.id?.toString?.() || t.id,
          name: t.title || t.name,
          type: 0,
          description: "", // Não incluir descrição da tarefa - só virá de avaliações existentes
          justification: "",
          tag: undefined as unknown as Tag,
          tagId: "",
          createdAt: t.createdAt || "",
          appointmentId: "",
        }));

        // Buscar avaliações do dia para associar às tarefas
        const today = new Date();
        const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
        const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
        this.loadDailyAppointmentsForLists(startDate, endDate);
      },
      error: () => {
        this.tasksForList = [];
      },
    });
  }

  // Carrega metas do colaborador e mapeia para Activity para reutilizar o template
  private loadGoalsForPerson(trackInit = false): void {
    this.withInitTracking(this.goalsService.findAllByPersonId(this.personId), trackInit).subscribe({
      next: (goals: any[]) => {
        if (goals !== null && goals.length > 0) {
          this.goalsForList = goals.map((g) => ({
            id: g.id?.toString?.() || g.id,
            name: g.name || g.title,
            type: 0,
            description: "", // Não incluir descrição da meta - só virá de avaliações existentes
            justification: "",
            tag: undefined as unknown as Tag,
            tagId: "",
            createdAt: g.createdAt || "",
            appointmentId: "",
          }));

          // Buscar avaliações do dia para associar às metas
          const today = new Date();
          const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
          const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
          this.loadDailyAppointmentsForLists(startDate, endDate);
        }
      },
      error: () => {
        this.goalsForList = [];
      },
    });
  }

  // Permite trocar o tipo de lista a partir do template
  onChangeListType(type: "ROTINAS" | "TAREFAS" | "METAS"): void {
    this.selectedListType = type;
  }

  openReportsModal(): void {
    // Resetar o formulário
    this.formReport.reset();
    const modalElement = document.getElementById("reportsModal");
    if (modalElement) {
      const modalInstance = this.modalService.getInstance(modalElement) || this.modalService.createModal(modalElement);
      modalInstance.show();
    }
  }

  onSubmitReportForm(): void {
    if (this.formReport.invalid) {
      return;
    }

    this.isLoadingReport = true;
    this.generateReportPDF();
  }

  async generateReportPDF(): Promise<void> {
    if (!this.personId) {
      this.toast.error("Funcionário não encontrado", "Erro");
      this.isLoadingReport = false;
      return;
    }

    this.appointmentService
      .findViewsByPersonIdAndCreatedAtBetween(
        this.personId,
        this.formReport.get("startDate").value,
        this.formReport.get("endDate").value,
      )
      .subscribe({
        next: async (response) => {
          if (response !== null && response.length > 0) {
            const appointments: PersonAppointmentRoutineTask[] = response;
            const pdf = new jsPDF("p", "mm", "a4");

            // Título principal
            pdf.setFontSize(20);
            pdf.setTextColor(0);
            pdf.text("Relatório de Avaliações", 10, 30);

            // Configurações de layout
            const pageHeight = pdf.internal.pageSize.height;
            let yPosition = 50;
            pdf.setLineWidth(0.1);

            // Cabeçalho
            pdf.setFontSize(10);
            pdf.setTextColor(150);
            pdf.text("Relatório de Avaliações", 10, 10);
            pdf.text(`Página ${pdf.internal.pages.length - 1}`, 180, 10);

            // Lista de avaliações
            pdf.setFontSize(12);
            pdf.setTextColor(30);
            pdf.text("Avaliações:", 10, 45);

            // Iteração dos itens
            appointments.forEach((item, index) => {
              if (yPosition > pageHeight - 30) {
                pdf.addPage();
                yPosition = 20;
                pdf.setFontSize(10);
                pdf.setTextColor(150);
                pdf.text("Relatório de Avaliações", 10, 10);
                pdf.text(`Página ${pdf.internal.pages.length - 1}`, 180, 10);
                pdf.setTextColor(30);
              }

              const personText = `${index + 1}. ${item.name || "Sem nome"}`;
              const createdAtText = `Data: ${item.createdAt || ""}`;
              const routineText = `Rotina: ${item.routine || "-"}`;
              const descriptionText = `Descrição: ${item.description || "-"}`;
              const justificationText = `Justificativa: ${item.justification || "-"}`;
              const tagText = `Cor: ${item.tag || "-"}`;

              // Nome da pessoa em negrito
              pdf.setFontSize(11);
              pdf.setFont("helvetica", "bold");
              pdf.text(personText, 10, yPosition);

              // Campos adicionais
              pdf.setFontSize(10);
              pdf.setFont("helvetica", "normal");

              // Cores das tags
              if (tagText == "Cor: Vermelho") {
                pdf.setTextColor("#FF0000");
                pdf.text(tagText + " (Falha grave)", 10, yPosition + 7);
              }
              if (tagText == "Cor: Laranja") {
                pdf.setTextColor("#FF7514");
                pdf.text(tagText + " (Alerta - erro cometido as vezes)", 10, yPosition + 7);
              }
              if (tagText == "Cor: Amarelo") {
                pdf.setTextColor("#FFFF00");
                pdf.text(tagText + " (Atenção)", 10, yPosition + 7);
              }
              if (tagText == "Cor: Verde") {
                pdf.setTextColor("#008000");
                pdf.text(tagText + " (Dever cumprido)", 10, yPosition + 7);
              }
              if (tagText == "Cor: Azul") {
                pdf.setTextColor("#0000FF");
                pdf.text(tagText + " (Acima da média)", 10, yPosition + 7);
              }
              if (tagText == "Cor: Roxo") {
                pdf.setTextColor("#A020F0");
                pdf.text(tagText + " (Ótimo, Parabéns, Excelente)", 10, yPosition + 7);
              }
              if (tagText == "Cor: Cinza") {
                pdf.text(tagText + " (Atestado médico ou Férias)", 10, yPosition + 7);
              }
              if (tagText == "Cor: Preto") {
                pdf.text(tagText, 10, yPosition + 7);
              }

              pdf.setTextColor("#000000");
              pdf.text(createdAtText, 10, yPosition + 14);
              pdf.text(routineText, 10, yPosition + 21);
              pdf.text(descriptionText, 10, yPosition + 28);
              pdf.text(justificationText, 10, yPosition + 34);

              // Linha de separação
              yPosition += 45;
              pdf.setDrawColor(220);
              pdf.line(10, yPosition - 5, 200, yPosition - 5);
              yPosition += 5;
            });

            // Salvar o PDF
            pdf.save(`${appointments[0].name}-${appointments[0].createdAt}.pdf`);

            this.isLoadingReport = false;
            this.cdr.detectChanges(); // Forçar detecção de mudanças para OnPush
            this.openReportSuccessModal();
            this.closeReportsModal();
          } else {
            // Salvar o período selecionado para exibir na mensagem (formatar as datas)
            const startDate = this.formReport.get("startDate").value;
            const endDate = this.formReport.get("endDate").value;
            this.reportPeriod = {
              startDate: startDate ? this.datePipe.transform(startDate, "dd/MM/yyyy") || startDate : "",
              endDate: endDate ? this.datePipe.transform(endDate, "dd/MM/yyyy") || endDate : "",
            };
            this.isLoadingReport = false;
            this.cdr.detectChanges(); // Forçar detecção de mudanças para OnPush
            this.openReportNoContentModal();
          }
        },
        error: (err) => {
          this.logger.error("Error generating report", err);
          this.errorHandlerService.handle(err);
          this.isLoadingReport = false;
          this.cdr.detectChanges(); // Forçar detecção de mudanças para OnPush
        },
      });
  }

  closeReportsModal(): void {
    this.formReport.reset();
    const modalElement = document.getElementById("reportsModal");
    if (modalElement) {
      const modalInstance = this.modalService.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
  }

  openReportSuccessModal(): void {
    const modalElement = document.getElementById("reportSuccessModal");
    if (modalElement) {
      const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
      modalInstance.show();
    }
  }

  openReportNoContentModal(): void {
    const modalElement = document.getElementById("reportNoContentModal");
    if (modalElement) {
      const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
      modalInstance.show();
    }
  }

  private withInitTracking<T>(source$: Observable<T>, trackInit = false): Observable<T> {
    if (!trackInit) {
      return source$;
    }

    this.pendingInitRequests++;
    if (this.pendingInitRequests === 1) {
      this.isInitialDataReady = false;
      this.initialLoad$.next(false);
      this.cdr.markForCheck();
    }

    return source$.pipe(
      finalize(() => {
        this.pendingInitRequests = Math.max(0, this.pendingInitRequests - 1);
        if (this.pendingInitRequests === 0) {
          this.isInitialDataReady = true;
          this.initialLoad$.next(true);
          this.cdr.markForCheck();
        }
      }),
    );
  }

  // ABSTINENCE

  onSubmitFormAbstinence(abstinenceData: AbstinenceData) {
    this.isSavingAbstinence = true;
    const abstinence: AbstinenceRequest = {
      id: abstinenceData.id != null ? abstinenceData.id : null,
      personId: this.personId,
      description: abstinenceData.description,
      startDate: abstinenceData.startDate,
      endDate: abstinenceData.endDate,
    };

    if (abstinenceData.id) {
      // update
      this.isSavingAbstinence = false;
      return;
    }

    // create
    this.abstinenceService.create(abstinence, this.selectedAbstinenceFile).subscribe({
      next: () => this.toast.success("Atestado médico cadastrado com sucesso", "Cadastro"),
      error: (error) => this.errorHandlerService.handle(error),
      complete: () => (this.isSavingAbstinence = false),
    });
  }
}
